// Shared resolution of "additional information" (supporting materials like formula sheets, diagrams,
// shared tables) for the MS-generation, GS-generation, and grading agents.
//
// Following the same content/image model used for solutions: an item's text lives in its `content`
// (the concatenated OCR text of its text/both boxes; image boxes are text-free by the OCR guard) and
// its images come from its image/both boxes. So "pass text if there is text, pass images if there are
// images" falls out naturally per item: text-only -> text, image-only -> image, both -> both.

import { logger } from '@adornis/base/logging.js';
import { imageBoxImageIds } from '../../../shared/bounding-box-content.js';

export interface AdditionalInfoPromptItem {
  id: string;
  content: string;
  imageIds: string[];
}

export function normalizeAdditionalInfoForPrompt(input: any[] | undefined): AdditionalInfoPromptItem[] {
  if (!Array.isArray(input)) return [];

  return input
    .map(item => {
      const source = item?.doc ?? item;
      return {
        id: String(source?._id ?? source?.id ?? '').trim(),
        content: String(source?.content ?? '').trim(),
        imageIds: imageBoxImageIds(Array.isArray(source?.boundingBoxes) ? source.boundingBoxes : []),
      };
    })
    .filter(item => item.id.length > 0 || item.content.length > 0 || item.imageIds.length > 0);
}

// All additionalInfo ids referenced by any task in the tree — used to decide which items are
// "globally shared" (referenced by no specific task) vs task-linked.
export function collectAllTaskAdditionalInfoIds(tasks: any[] | undefined): Set<string> {
  const ids = new Set<string>();
  const walk = (list: any[] | undefined) => {
    for (const task of Array.isArray(list) ? list : []) {
      for (const rawId of Array.isArray(task?.additionalInfoIds) ? task.additionalInfoIds : []) {
        const id = String(rawId ?? '').trim();
        if (id) ids.add(id);
      }
      walk(task?.subTasks);
    }
  };
  walk(tasks);
  return ids;
}

// The additional info relevant to a task: everything it explicitly links via additionalInfoIds, plus
// any globally-shared info (info linked by no specific task). Deduplicated.
export function resolveAdditionalInfoForTask(
  task: any,
  allAdditionalInfo: AdditionalInfoPromptItem[],
  allTaskAdditionalInfoIds: Set<string>,
): AdditionalInfoPromptItem[] {
  const taskAdditionalInfoIds: Set<string> = new Set(
    (Array.isArray(task?.additionalInfoIds) ? task.additionalInfoIds : [])
      .map((rawId: unknown) => String(rawId ?? '').trim())
      .filter(Boolean),
  );

  const linked = allAdditionalInfo.filter(info => info.id && taskAdditionalInfoIds.has(info.id));
  const globalShared = allAdditionalInfo.filter(info => !info.id || !allTaskAdditionalInfoIds.has(info.id));

  // A task can reference an id the current `additionalInformation` pool no longer has — e.g. a material
  // whose `_id` changed on a refinement/re-extraction (materials have no id-stability guarantee the way
  // task numbers do) or one deleted on one side of the structure editor's two independent save mutations
  // without the other side's cleanup landing too. Silently dropping it means MS-generation/GS-generation/
  // grading run without content the task was actually supposed to have, with nothing to say why — logged
  // here since this function is the one place shared by all three callers.
  if (linked.length < taskAdditionalInfoIds.size) {
    const resolvedIds = new Set(linked.map(info => info.id));
    const missing = [...taskAdditionalInfoIds].filter(id => !resolvedIds.has(id));
    logger.warn(
      { taskId: task?._id, taskNumber: task?.taskNumber, missing },
      'A task references additionalInfoIds not present in the current additionalInformation pool — dropped silently otherwise',
    );
  }

  const seen = new Set<string>();
  return [...linked, ...globalShared].filter(info => {
    const key = `${info.id}::${info.imageIds.join('|')}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// A numbered text block of the items that carry text, for injection into a prompt. Empty string when
// none have text (so callers can conditionally render the section). Image-only items contribute no
// text here — their images are attached separately via additionalInfoImageIds().
export function formatAdditionalInfoText(items: AdditionalInfoPromptItem[]): string {
  const withText = items.filter(item => item.content.length > 0);
  if (withText.length === 0) return '';
  return withText.map((item, index) => `[${index + 1}] ${item.content}`).join('\n');
}

// Flat list of image ids across the items (image/both boxes), for attaching as images.
export function additionalInfoImageIds(items: AdditionalInfoPromptItem[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    for (const id of item.imageIds) {
      if (!seen.has(id)) {
        seen.add(id);
        ids.push(id);
      }
    }
  }
  return ids;
}
